import type { ProductUnit, VariantOptionValue, VatType } from '@/api/models';
import { orderedVariantOptions } from '@/pages/products/components/productDetail/productDetailMeta';
import type {
    DraftInvoiceExpenseRequest,
    DraftInvoiceLineMode,
    DraftInvoiceLineRequest,
    DraftInvoiceMethod,
    DraftInvoiceOptionValueRequest,
    DraftInvoicePricingMode,
    DraftInvoiceRequest,
} from '@/api/handlers/resources/purchases/requests';
import type {
    DraftInvoiceLine,
    GetDraftInvoiceResponse,
} from '@/api/handlers/resources/purchases/responses';
import { parseGelToTetri, tetriToDecimalString } from '@/utils/money';
import { formatCurrencyValue } from '@/utils/currency';
import {
    DEFAULT_UNIT,
    DEFAULT_VAT,
    UNIT_LABEL_KEY,
    UNIT_OPTIONS,
    VAT_LABEL_KEY,
    VAT_OPTIONS,
} from '@/pages/products/components/createProduct/createProductMeta';

/**
 * Types + pure helpers for the manual-import form. All field state is held as
 * raw strings and parsed at assemble time by {@link buildManualImportRequest}.
 * Summary math and validation are pure so they can be unit-tested directly.
 *
 * A line is either an `existing` restock (a catalog variant — identity, catalog
 * fields and variation are prefilled from the picked variant and shown
 * read-only) or a `new` product defined inline (every field editable). Both
 * modes live in one shape so the grid can drive a single set of handlers;
 * {@link validateManualImport} enforces the per-mode required fields.
 *
 * The editable numerics — quantity, cost, sale price, optional wholesale price
 * and optional minimum quantity (restock alert threshold) — apply to both modes.
 * On import the backend writes them back to the variant (existing) or sets them
 * on the created variant (new); `useManualImport` drives the real draft and
 * import mutations.
 */

export {
    UNIT_OPTIONS,
    VAT_OPTIONS,
    UNIT_LABEL_KEY,
    VAT_LABEL_KEY,
    DEFAULT_UNIT,
    DEFAULT_VAT,
};

export const IMPORT_CURRENCY = 'GEL';

/**
 * How a variant reads in the picker: `Black / L · TEE-BLK-L`.
 *
 * The barcode alone was the whole label, and variants of one product are
 * allowed to share a physical barcode — that is what `BarcodeDiff` is for — so
 * two genuinely different variants showed up as two identical `TEE-BLK-M`
 * rows with no way to tell them apart (BOS-139). The option values in the
 * product's own axis order are what distinguish them; the unique key is what
 * identifies them.
 *
 * A simple product has no options, so it falls through to the key alone.
 */
export const variantPickerLabel = (variant: {
    barcode: string;
    uniqueKey?: string;
    options?: readonly VariantOptionValue[];
}): string => {
    const key = variant.uniqueKey || variant.barcode;
    const options = orderedVariantOptions(variant.options ?? []);
    return options.length === 0
        ? key
        : `${options.map((option) => option.valueName).join(' / ')} · ${key}`;
};

const MARKUP_FORMATTER = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
});

/**
 * Markup over cost as a percentage. A percentage, not money — it carries no
 * currency symbol, and the arithmetic lives here rather than in the panel
 * (BOS-162 moved the last `toFixed` out of the render).
 */
export const formatMarkupPercent = (value: number): string =>
    `${MARKUP_FORMATTER.format(value)}%`;

/** Presentational currency formatter shared by the import widgets. */
export const formatImportMoney = (value: number): string =>
    formatCurrencyValue(IMPORT_CURRENCY, value) || `${IMPORT_CURRENCY} 0`;

/** Resolve a display name from an `{ id, name }` list, '' when unset / absent. */
export const optionName = (
    options: { id: string; name: string }[],
    id: string | null,
): string => (id ? (options.find((o) => o.id === id)?.name ?? '') : '');

export type ImportLineMode = DraftInvoiceLineMode;

/** How the sale price is entered, form-wide: a fixed amount (markup derived)
 *  or a markup % on cost (sale price derived). */
export type SalePricingMode = DraftInvoicePricingMode;

export type ImportLineDraft = {
    key: string;
    mode: ImportLineMode;
    // existing: the picked catalog variant; new: empty
    variantId: string | null;
    productId: string | null;
    // displayed (read-only) for existing; editable for new
    name: string;
    sku: string;
    barcode: string;
    // catalog fields — editable for new lines, prefilled (read-only) for existing
    categoryId: string | null;
    brandId: string | null;
    unit: ProductUnit;
    vatType: VatType;
    // variation: attributeTypeId → chosen attributeValueId for this single variant
    // ('' for an axis with no value yet). Editable for new lines, prefilled for
    // existing.
    optionValues: Record<string, string>;
    // shared editable numerics (raw strings)
    quantity: string;
    purchasePrice: string;
    salePrice: string;
    // markup % on cost — derived from cost/sale in fixed mode, the input that
    // drives salePrice in markup mode ('' when not computable)
    markup: string;
    // optional wholesale price (raw string; '' when unset)
    wholesalePrice: string;
    // optional minimum quantity — drives restock alerts ('' when unset)
    minimumQuantity: string;
    batchNumber: string;
    // optional dates (raw 'YYYY-MM-DD' strings)
    expiryDate: string;
    saleDueDate: string;
};

export type ExpenseDraft = {
    key: string;
    label: string;
    amount: string;
};

export type ManualImportFormState = {
    supplierId: string | null;
    branchId: string | null;
    invoiceNumber: string;
    date: string;
    currency: string;
    pricingMode: SalePricingMode;
    lines: ImportLineDraft[];
    expenses: ExpenseDraft[];
};

/** Common expense labels offered as suggestions (free text still allowed). */
export const EXPENSE_SUGGESTION_KEYS = [
    'transportation',
    'customs',
    'loadingUnloading',
    'bankFee',
    'storage',
    'other',
] as const;

export type ExpenseSuggestionKey = (typeof EXPENSE_SUGGESTION_KEYS)[number];

export const EXPENSE_SUGGESTION_LABEL_KEY: Record<
    ExpenseSuggestionKey,
    string
> = {
    transportation:
        'purchases.manualImport.expenses.suggestions.transportation',
    customs: 'purchases.manualImport.expenses.suggestions.customs',
    loadingUnloading:
        'purchases.manualImport.expenses.suggestions.loadingUnloading',
    bankFee: 'purchases.manualImport.expenses.suggestions.bankFee',
    storage: 'purchases.manualImport.expenses.suggestions.storage',
    other: 'purchases.manualImport.expenses.suggestions.other',
};

/** Catalog snapshot of a picked variant, used to prefill an existing line. */
export type ExistingLinePick = {
    variantId: string;
    productId: string;
    name: string;
    sku: string | null;
    barcode: string;
    categoryId: string | null;
    brandId: string | null;
    unit: ProductUnit;
    vatType: VatType;
    optionValues: Record<string, string>;
    salePrice: number;
    wholesalePrice: number | null;
    minimumQuantity: number;
};

/** Format a positive numeric prefill as a raw string, blank when ≤ 0 / null. */
const prefillNumber = (value: number | null): string =>
    value !== null && value > 0 ? String(value) : '';

export const makeExistingLine = (
    key: string,
    picked: ExistingLinePick,
): ImportLineDraft => ({
    key,
    mode: 'existing',
    variantId: picked.variantId,
    productId: picked.productId,
    name: picked.name,
    sku: picked.sku ?? '',
    barcode: picked.barcode,
    categoryId: picked.categoryId,
    brandId: picked.brandId,
    unit: picked.unit,
    vatType: picked.vatType,
    optionValues: picked.optionValues,
    quantity: '',
    purchasePrice: '',
    salePrice: prefillNumber(picked.salePrice),
    markup: '',
    wholesalePrice: prefillNumber(picked.wholesalePrice),
    minimumQuantity: prefillNumber(picked.minimumQuantity),
    batchNumber: '',
    expiryDate: '',
    saleDueDate: '',
});

export const makeNewLine = (key: string): ImportLineDraft => ({
    key,
    mode: 'new',
    variantId: null,
    productId: null,
    name: '',
    sku: '',
    barcode: '',
    categoryId: null,
    brandId: null,
    unit: DEFAULT_UNIT,
    vatType: DEFAULT_VAT,
    optionValues: {},
    quantity: '',
    purchasePrice: '',
    salePrice: '',
    markup: '',
    wholesalePrice: '',
    minimumQuantity: '',
    batchNumber: '',
    expiryDate: '',
    saleDueDate: '',
});

export const makeExpense = (key: string): ExpenseDraft => ({
    key,
    label: '',
    amount: '',
});

export const makeInitialState = (): ManualImportFormState => ({
    supplierId: null,
    branchId: null,
    invoiceNumber: '',
    date: '',
    currency: IMPORT_CURRENCY,
    pricingMode: 'fixed',
    lines: [],
    expenses: [],
});

/* -------------------------------------------------------------------------- */
/* Sale price ⇄ markup                                                        */
/* -------------------------------------------------------------------------- */

/** Strict optional parse for derivation: '' / non-numeric → null. */
const parseOptionalNumber = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Two decimal places, for a **percentage**. Not for money: a markup of 33.33%
 * is a ratio the user typed, and rounding it is presentation.
 */
const roundPercentTo2 = (percent: number): number =>
    Math.round(percent * 100) / 100;

/** Markup % implied by cost + sale price, '' when not computable (no cost). */
export const computeLineMarkup = (
    purchasePrice: string,
    salePrice: string,
): string => {
    const cost = parseOptionalNumber(purchasePrice);
    const sale = parseOptionalNumber(salePrice);
    if (cost === null || cost <= 0 || sale === null) return '';
    return String(roundPercentTo2(((sale - cost) / cost) * 100));
};

/**
 * Sale price implied by cost + markup %, '' when either side is missing.
 *
 * Derived **in tetri** (BOS-171). It used to be
 * `Math.round(cost * (1 + percent / 100) * 100) / 100` — a float amount rounded
 * by scaling, which is the one thing the money contract forbids, and which the
 * lint rule had been walking past because the multiply hid behind
 * `Math.round(...)` on a variable named `value`. The cost is parsed to an exact
 * integer, the markup applied to that integer, and the result rendered back
 * through `tetriToDecimalString` — so the field the user sees is a rounding of
 * an exact number rather than a rounding of a float.
 */
export const computeLineSalePrice = (
    purchasePrice: string,
    markup: string,
): string => {
    const cost = parseGelToTetri(purchasePrice.trim());
    const percent = parseOptionalNumber(markup);
    if (!cost.ok || percent === null) return '';
    const saleTetri = Math.round(cost.tetri * (1 + percent / 100));
    return tetriToDecimalString(saleTetri);
};

/** Re-derive the dependent pricing field after a cost/sale/markup edit:
 *  fixed mode owns salePrice (markup follows), markup mode the reverse. */
export const syncLinePricing = (
    line: ImportLineDraft,
    mode: SalePricingMode,
): ImportLineDraft =>
    mode === 'markup'
        ? {
              ...line,
              salePrice: computeLineSalePrice(line.purchasePrice, line.markup),
          }
        : {
              ...line,
              markup: computeLineMarkup(line.purchasePrice, line.salePrice),
          };

/** Narrow a ToggleButtonGroup selection to a pricing mode (defaults fixed). */
export const toSalePricingMode = (keys: Iterable<unknown>): SalePricingMode => {
    const [first] = keys;
    return first === 'markup' ? 'markup' : 'fixed';
};

/* -------------------------------------------------------------------------- */
/* Summary                                                                    */
/* -------------------------------------------------------------------------- */

export type ImportSummary = {
    productsTotal: number;
    extraExpenses: number;
    totalCost: number;
    totalSales: number;
    /** Markup on cost as a percentage, or null when total cost is 0. */
    markupPercent: number | null;
};

const toNumber = (value: string): number => {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : 0;
};

export const computeSummary = (state: ManualImportFormState): ImportSummary => {
    let productsTotal = 0;
    let totalSales = 0;
    for (const line of state.lines) {
        const quantity = toNumber(line.quantity);
        productsTotal += toNumber(line.purchasePrice) * quantity;
        totalSales += toNumber(line.salePrice) * quantity;
    }
    const extraExpenses = state.expenses.reduce(
        (sum, expense) => sum + toNumber(expense.amount),
        0,
    );
    const totalCost = productsTotal + extraExpenses;
    const markupPercent =
        totalCost > 0 ? ((totalSales - totalCost) / totalCost) * 100 : null;

    return {
        productsTotal,
        extraExpenses,
        totalCost,
        totalSales,
        markupPercent,
    };
};

/* -------------------------------------------------------------------------- */
/* Dirty + validation                                                         */
/* -------------------------------------------------------------------------- */

/** Dirty = anything entered. Drives Save-Draft availability. */
export const isDirty = (state: ManualImportFormState): boolean =>
    state.supplierId !== null ||
    state.branchId !== null ||
    state.invoiceNumber.trim() !== '' ||
    state.date.trim() !== '' ||
    state.lines.length > 0 ||
    state.expenses.length > 0;

const isPositive = (value: string): boolean => toNumber(value) > 0;
const isNonNegative = (value: string): boolean => toNumber(value) >= 0;

/**
 * Light client-side validation for the Import action. Returns the i18n key of
 * the first problem, or `null` when importable. The server still owns
 * cross-entity validation (variant/supplier/branch existence) once wired.
 */
export const validateManualImport = (
    state: ManualImportFormState,
): string | null => {
    if (!state.supplierId) {
        return 'purchases.manualImport.errors.supplierRequired';
    }
    if (!state.branchId) {
        return 'purchases.manualImport.errors.branchRequired';
    }
    if (!state.invoiceNumber.trim()) {
        return 'purchases.manualImport.errors.invoiceNumberRequired';
    }
    if (!state.date.trim()) {
        return 'purchases.manualImport.errors.dateRequired';
    }
    if (state.lines.length === 0) {
        return 'purchases.manualImport.errors.linesRequired';
    }
    for (const line of state.lines) {
        if (line.mode === 'existing' && !line.variantId) {
            return 'purchases.manualImport.errors.lineProductRequired';
        }
        if (line.mode === 'new') {
            if (!line.name.trim()) {
                return 'purchases.manualImport.errors.lineNameRequired';
            }
            if (!line.sku.trim()) {
                return 'purchases.manualImport.errors.lineSkuRequired';
            }
            if (!line.barcode.trim()) {
                return 'purchases.manualImport.errors.lineBarcodeRequired';
            }
            // Category and brand are optional; the server resolves/creates them.
        }
        if (!isPositive(line.quantity)) {
            return 'purchases.manualImport.errors.lineQuantityRequired';
        }
        // The cost must actually be present, not blank-coerced to 0 — a blank
        // one assembles to null, which the strict import endpoint rejects.
        const purchasePrice = parseOptionalNumber(line.purchasePrice);
        if (purchasePrice === null || purchasePrice < 0) {
            return 'purchases.manualImport.errors.linePurchasePriceRequired';
        }
        // The sale price must actually be present, not blank-coerced to 0. In
        // markup mode it is derived and read-only, so a blank one means the
        // markup (or the cost it needs) is missing — point the user there.
        const salePrice = parseOptionalNumber(line.salePrice);
        if (salePrice === null || salePrice < 0) {
            return state.pricingMode === 'markup'
                ? 'purchases.manualImport.errors.lineMarkupRequired'
                : 'purchases.manualImport.errors.lineSalePriceRequired';
        }
    }
    for (const expense of state.expenses) {
        if (!expense.label.trim()) {
            return 'purchases.manualImport.errors.expenseLabelRequired';
        }
        if (!isNonNegative(expense.amount)) {
            return 'purchases.manualImport.errors.expenseAmountRequired';
        }
    }
    return null;
};

/* -------------------------------------------------------------------------- */
/* Request contract (the draft-invoices wire body, see api/…/purchases)       */
/* -------------------------------------------------------------------------- */

/** One chosen variation value sent for a new product's single variant. */
export type ManualImportOptionValueRequest = DraftInvoiceOptionValueRequest;

export type ManualImportLineRequest = DraftInvoiceLineRequest;

export type ManualImportExpenseRequest = DraftInvoiceExpenseRequest;

/** How the import draft was produced — manual entry or an analyzed Excel file. */
export type ManualImportMethod = DraftInvoiceMethod;

export type ManualImportRequest = DraftInvoiceRequest;

const trimOrNull = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
};

/** Parse an optional numeric field: '' (unset) → null, otherwise the number. */
const toNumberOrNull = (value: string): number | null =>
    value.trim() === '' ? null : toNumber(value);

/** Drop axes with no chosen value, then flatten to the request's pair list. */
const toOptionValueRequests = (
    optionValues: Record<string, string>,
): ManualImportOptionValueRequest[] =>
    Object.entries(optionValues)
        .filter(([, attributeValueId]) => attributeValueId.trim() !== '')
        .map(([attributeTypeId, attributeValueId]) => ({
            attributeTypeId,
            attributeValueId,
        }));

const buildLineRequest = (line: ImportLineDraft): ManualImportLineRequest => ({
    // Both modes carry the full catalog snapshot (drafts echo it back on
    // resume, so an existing line keeps its display name/sku/category);
    // `mode` + `variantId` tell the server which identity wins. Numerics are
    // nullable on the wire — a blank input round-trips as null, not 0.
    mode: line.mode,
    variantId: line.mode === 'existing' ? line.variantId : null,
    name: trimOrNull(line.name),
    sku: trimOrNull(line.sku),
    barcode: trimOrNull(line.barcode),
    categoryId: line.categoryId,
    brandId: line.brandId,
    unit: line.unit,
    vatType: line.vatType,
    optionValues: toOptionValueRequests(line.optionValues),
    quantity: toNumberOrNull(line.quantity),
    purchasePrice: toNumberOrNull(line.purchasePrice),
    salePrice: toNumberOrNull(line.salePrice),
    wholesalePrice: toNumberOrNull(line.wholesalePrice),
    minimumQuantity: toNumberOrNull(line.minimumQuantity),
    batchNumber: trimOrNull(line.batchNumber),
    expiryDate: trimOrNull(line.expiryDate),
    saleDueDate: trimOrNull(line.saleDueDate),
});

/** Assemble the draft-invoices POST/PUT body from the raw form state. */
export const buildManualImportRequest = (
    state: ManualImportFormState,
    method: ManualImportMethod = 'manual',
): ManualImportRequest => ({
    method,
    pricingMode: state.pricingMode,
    supplierId: state.supplierId,
    branchId: state.branchId,
    invoiceNumber: trimOrNull(state.invoiceNumber),
    date: trimOrNull(state.date),
    currency: state.currency,
    lines: state.lines.map(buildLineRequest),
    expenses: state.expenses.map((expense) => ({
        label: expense.label.trim(),
        amount: toNumber(expense.amount),
    })),
});

/* -------------------------------------------------------------------------- */
/* Draft rehydration (GET /purchases/draft-invoices/{id} → form state)        */
/* -------------------------------------------------------------------------- */

/** Inverse of the optional-number parse: null → blank input. */
const numberToInput = (value: number | null): string =>
    value === null ? '' : String(value);

const draftLineToImportLine = (line: DraftInvoiceLine): ImportLineDraft => ({
    // Server line ids are stable + unique — reuse them as the React row keys
    // (the hook's generated `row-N` keys never collide with them).
    key: line.id,
    mode: line.mode,
    variantId: line.variantId,
    // Not on the wire; only the picker sets it, nothing reads it afterwards.
    productId: null,
    name: line.name ?? '',
    sku: line.sku ?? '',
    barcode: line.barcode ?? '',
    categoryId: line.categoryId,
    brandId: line.brandId,
    unit: line.unit ?? DEFAULT_UNIT,
    vatType: line.vatType ?? DEFAULT_VAT,
    optionValues: Object.fromEntries(
        line.optionValues.map((value) => [
            value.attributeTypeId,
            value.attributeValueId,
        ]),
    ),
    quantity: numberToInput(line.quantity),
    purchasePrice: numberToInput(line.purchasePrice),
    salePrice: numberToInput(line.salePrice),
    // Not stored server-side — re-derive from the stored cost + sale price.
    markup: computeLineMarkup(
        numberToInput(line.purchasePrice),
        numberToInput(line.salePrice),
    ),
    wholesalePrice: numberToInput(line.wholesalePrice),
    minimumQuantity: numberToInput(line.minimumQuantity),
    batchNumber: line.batchNumber ?? '',
    expiryDate: line.expiryDate ?? '',
    saleDueDate: line.saleDueDate ?? '',
});

/** Map a saved draft back into the editable form state (mechanical inverse
 *  of {@link buildManualImportRequest} — the server echoes a superset). */
export const draftToFormState = (
    draft: GetDraftInvoiceResponse,
): ManualImportFormState => ({
    supplierId: draft.supplierId,
    branchId: draft.branchId,
    invoiceNumber: draft.invoiceNumber ?? '',
    date: draft.date ?? '',
    currency: draft.currency || IMPORT_CURRENCY,
    pricingMode: draft.pricingMode,
    lines: draft.lines.map(draftLineToImportLine),
    expenses: draft.expenses.map((expense) => ({
        key: expense.id,
        label: expense.label,
        amount: numberToInput(expense.amount),
    })),
});
